/**
 * 标准配合候选求解结果构建共享模块。
 *
 * 文件职责：
 * 1. 为约束图闭合候选与全局数值内核提供同一套"刚体写回 + 整轮 validation + 总体状态"结果构建，
 *    保证所有候选生成器的接受判据同源（避免图求解器与数值内核各自拼装结果造成语义漂移）；
 * 2. 抽出为独立模块以消除"图求解器 ↔ 数值内核"的循环依赖；
 * 3. 只消费 plain-data 与既有校验/几何模块，不含任何候选生成逻辑。
 */

import * as THREE from 'three';
import {
  MateSolveStatus,
  type AssemblyMateDefinition,
} from '../definition';
import { resolveStandardMateLinearDimensionScale } from './dimension/StandardMateDimension';
import { validateSolvedMateDefinitions } from './validation/StandardMateConstraintValidator';
import { resolveStandardMateSolverOverallStatus } from './StandardMateSolverStatus';
import { resolveStandardMateConstraintTolerance } from './StandardMateConstraintResidual';
import {
  applyWorldDeltaToTransform,
  composeMatrix,
  decomposeMatrix,
} from './geometry/StandardMateGeometryAccess';
import type { ComponentWorldDeltaMap } from './geometry/StandardMateGeometryTransform';
import type {
  AssemblyMateSolveDiagnostic,
  AssemblyMateSolverComponentState,
  StandardMateSolverInput,
  StandardMateSolverResult,
} from './StandardMateSolverTypes';

/**
 * 使用世界刚体 delta 更新组件状态。
 *
 * @param component 原组件状态。
 * @param delta 世界刚体 delta。
 * @returns 更新后的组件状态。
 */
export function transformComponentStateByWorldDelta(
  component: AssemblyMateSolverComponentState,
  delta: THREE.Matrix4,
): AssemblyMateSolverComponentState {
  const nextTransform = applyWorldDeltaToTransform(component.transform, delta, component.worldTransform);
  const nextWorldTransform = component.worldTransform
    ? {
        ...decomposeMatrix(delta.clone().multiply(composeMatrix(component.worldTransform))),
        // 刚体变换保留原世界缩放，避免 decompose 累积缩放漂移。
        scale: [...component.worldTransform.scale] as readonly [number, number, number],
      }
    : undefined;
  return {
    ...component,
    transform: nextTransform,
    ...(nextWorldTransform ? { worldTransform: nextWorldTransform } : {}),
  };
}

/**
 * 创建全部 Mate 已求解的基础诊断。
 *
 * @param mateDefinitions Mate Definition 列表。
 * @returns 诊断集合。
 */
export function createSolvedDiagnostics(
  mateDefinitions: readonly AssemblyMateDefinition[],
): readonly AssemblyMateSolveDiagnostic[] {
  return mateDefinitions.map((mate) => ({
    mateId: mate.mateId,
    status: MateSolveStatus.Solved,
  }));
}

/**
 * 按候选位姿构建经过整轮 validation 的求解结果。
 *
 * 说明：候选生成器（闭合规则/数值内核）只负责提出组件位姿，接受与否由这里统一的
 * validation + 总体状态判定给出；epsilon 缺省回落 canonical 线性容差（单一容差源）。
 *
 * @param input 求解器输入。
 * @param componentTransforms 候选组件 transform 映射。
 * @param components 候选组件状态。
 * @param componentDeltaMap 相对当前位姿的组件 delta 映射。
 * @returns 求解结果。
 */
export function createGraphSolverResult(
  input: StandardMateSolverInput,
  componentTransforms: Readonly<Record<string, AssemblyMateSolverComponentState['transform']>>,
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  componentDeltaMap: ComponentWorldDeltaMap,
): StandardMateSolverResult {
  const transientMateIds = new Set(input.transientMateIds ?? []);
  const tolerance = resolveStandardMateConstraintTolerance({
    epsilon: input.epsilon,
    angleEpsilon: input.angleEpsilon,
  });
  const validationDiagnostics = validateSolvedMateDefinitions({
    mateDefinitions: input.mateDefinitions,
    components,
    componentTransforms,
    componentDeltaMap,
    linearDimensionScale: resolveStandardMateLinearDimensionScale(input.linearDimensionScale),
    transientMateIds: input.transientMateIds,
    epsilon: tolerance.linear,
    angleEpsilon: tolerance.angular,
  });
  const diagnostics = [
    ...createSolvedDiagnostics(input.mateDefinitions),
    ...validationDiagnostics,
  ];
  return {
    status: resolveStandardMateSolverOverallStatus(diagnostics, transientMateIds),
    componentTransforms,
    diagnostics,
  };
}
